import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { api } from './lib/api';

// Conversational Phase 5 — the live CV pane: the right half of the split screen. Renders the EXISTING render model
// (populated-only, blueprint-grouped — the same model the export uses) as a quiet paper-style document that refetches
// after every save (refreshKey), highlights the just-saved entry, and lets the user EDIT IN PLACE.
//
// TWO RULES (the split-screen failure modes, ruled out by construction):
//   1. Edits SAVED content only — the entry mid-interview lives in the conversation until saved.
//   2. Stays QUIET — paper render, one soft highlight, no controls competing with the conversation.
// FIREWALL: reads the model; writes ONLY the user's typed words through the two existing user-author endpoints
// (PATCH /api/bullets/:id text · PATCH /api/entries/:id title). Both stay craft-gated: a 402 opens the paywall and
// the open editor keeps the user's words ("unlock to save") — nothing is lost, nothing is composed.

interface MBullet { id: string; text: string }
interface MEntry { id: string; archetype: 'experience' | 'credential'; title: string | null; organization: string | null; dateText: string; bullets: MBullet[] }
interface MSection { id: string; name: string; entries: MEntry[] }
interface Model { contact: { name: string; email: string; phone: string; city: string }; sections: MSection[] }

type EditTarget = { kind: 'bullet'; id: string; text: string } | { kind: 'title'; id: string; text: string } | null;

export function CvPane({ cvId, refreshKey, highlightEntryId, onPaywall, onCount }: {
  cvId: string;               // '' = nothing saved yet (author mode before the first save)
  refreshKey: number;         // bumped by the conversation after every save → refetch
  highlightEntryId: string;   // the just-saved entry — soft accent + scroll into view
  onPaywall: (cvId: string | null) => void;
  onCount?: (entries: number) => void; // feeds the narrow-mode tab badge
}) {
  const [model, setModel] = useState<Model | null>(null);
  const [edit, setEdit] = useState<EditTarget>(null);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState('');   // per-editor note (402 / error), keeps the user's words on screen
  const [localKey, setLocalKey] = useState(0); // bump after an in-pane edit saves → refetch
  const highlightRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!cvId) { setModel(null); onCount?.(0); return; }
    void (async () => {
      const r = await api<{ model: Model }>(`/api/cvs/${cvId}/model`);
      if (!r.ok) return;
      setModel(r.data.model);
      onCount?.(r.data.model.sections.reduce((n, s) => n + s.entries.length, 0));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cvId, refreshKey, localKey]);

  // The just-saved entry scrolls into view once it's in the fetched model. Guard: jsdom (tests) lacks scrollIntoView.
  useEffect(() => {
    const el = highlightRef.current;
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [model, highlightEntryId]);

  async function saveEdit() {
    if (!edit) return;
    const text = edit.text.trim();
    if (!text) { setNote('Nothing to save — the line is empty.'); return; }
    setSaving(true); setNote('');
    const r = edit.kind === 'bullet'
      ? await api(`/api/bullets/${edit.id}`, { method: 'PATCH', body: JSON.stringify({ text }) })
      : await api(`/api/entries/${edit.id}`, { method: 'PATCH', body: JSON.stringify({ title: text }) });
    setSaving(false);
    if (r.status === 402) { onPaywall(cvId); setNote('This CV needs its unlock to save — your words are kept; try again after.'); return; }
    if (!r.ok) { setNote('That didn’t save — check your connection and try again.'); return; }
    setEdit(null); setLocalKey((k) => k + 1);
  }

  if (!cvId || !model) return (
    <div style={S.paper} data-testid="cv-pane">
      <p style={S.empty} data-testid="cv-pane-empty">Your CV so far will appear here as you save entries — and you can edit it in place.</p>
    </div>
  );

  const editor = (target: EditTarget) => target && (
    <div style={S.editBox}>
      {target.kind === 'bullet'
        ? <textarea style={S.editInput} rows={3} autoFocus value={target.text} onChange={(e) => setEdit({ ...target, text: e.target.value })} data-testid="cv-pane-edit" />
        : <input style={S.editInput} autoFocus value={target.text} onChange={(e) => setEdit({ ...target, text: e.target.value })} data-testid="cv-pane-edit" />}
      <div style={S.editBar}>
        <button className="btn btn-primary" style={S.smallBtn} onClick={() => void saveEdit()} disabled={saving} data-testid="cv-pane-save">{saving ? 'Saving…' : 'Save'}</button>
        <button className="btn btn-ghost" style={S.smallBtn} onClick={() => { setEdit(null); setNote(''); }} data-testid="cv-pane-cancel">Cancel</button>
      </div>
      {note && <p style={S.note} data-testid="cv-pane-note">{note}</p>}
    </div>
  );

  return (
    <div style={S.paper} data-testid="cv-pane">
      <div style={S.header}>
        <p style={S.name}>{model.contact.name || '—'}</p>
        <p style={S.contactLine}>{[model.contact.city, model.contact.email, model.contact.phone].filter(Boolean).join(' · ')}</p>
      </div>
      {model.sections.map((s) => (
        <section key={s.id}>
          <p style={S.secHead}>{s.name}</p>
          {s.entries.map((e) => {
            const hl = e.id === highlightEntryId;
            return (
              <div key={e.id} ref={hl ? highlightRef : undefined} style={{ ...S.entry, ...(hl ? S.entryHl : {}) }} data-testid={`cv-pane-entry-${e.id}`}>
                {edit?.kind === 'title' && edit.id === e.id ? editor(edit) : (
                  <p style={S.entryTitle} onClick={() => { setEdit({ kind: 'title', id: e.id, text: e.title ?? '' }); setNote(''); }} title="Click to edit" data-testid={`cv-pane-title-${e.id}`}>
                    <strong>{e.title || '(untitled)'}</strong>
                    {e.organization ? <span> — {e.organization}</span> : null}
                    {e.dateText ? <span style={S.date}>  {e.dateText}</span> : null}
                  </p>
                )}
                {e.bullets.map((b) => (
                  edit?.kind === 'bullet' && edit.id === b.id ? <div key={b.id}>{editor(edit)}</div> : (
                    <p key={b.id} style={S.bullet} onClick={() => { setEdit({ kind: 'bullet', id: b.id, text: b.text }); setNote(''); }} title="Click to edit" data-testid={`cv-pane-bullet-${b.id}`}>
                      •&nbsp;{b.text}
                    </p>
                  )
                ))}
              </div>
            );
          })}
        </section>
      ))}
      <p style={S.hint}>Click any line to edit it — your words, always.</p>
    </div>
  );
}

const S: Record<string, CSSProperties> = {
  paper: { background: 'var(--card)', border: '1px solid var(--rule)', borderRadius: 8, boxShadow: 'var(--elev-raised)', padding: 'var(--space-5) var(--space-5)', fontFamily: 'var(--serif)', lineHeight: 1.45 },
  empty: { color: 'var(--muted)', fontFamily: 'var(--sans)', fontSize: 'var(--fs-body)', margin: 0 },
  header: { textAlign: 'center', marginBottom: 'var(--space-4)' },
  name: { fontSize: 'var(--fs-h2)', fontWeight: 600, margin: 0 },
  contactLine: { fontSize: 'var(--fs-label)', color: 'var(--muted)', margin: '2px 0 0' },
  secHead: { fontFamily: 'var(--sans)', fontSize: 'var(--fs-micro)', letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--muted)', borderBottom: '1px solid var(--rule)', paddingBottom: 3, margin: 'var(--space-4) 0 var(--space-2)' },
  entry: { padding: 'var(--space-1) var(--space-2)', margin: '0 -6px var(--space-2)', borderRadius: 6 },
  entryHl: { background: 'var(--surface-subtle)', outline: '1px solid var(--rule)' },
  entryTitle: { fontSize: 'var(--fs-body)', margin: '0 0 2px', cursor: 'pointer' },
  date: { color: 'var(--muted)', fontSize: 'var(--fs-label)', marginLeft: 8 },
  bullet: { fontSize: 13.5, margin: '2px 0 2px var(--space-3)', cursor: 'pointer' },
  editBox: { fontFamily: 'var(--sans)', margin: 'var(--space-1) 0' },
  editInput: { display: 'block', width: '100%', boxSizing: 'border-box', padding: 'var(--space-2)', fontSize: 'var(--fs-body)' },
  editBar: { display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-2)' },
  smallBtn: { fontSize: 'var(--fs-small)', padding: 'var(--space-1) var(--space-3)' },
  note: { fontFamily: 'var(--sans)', fontSize: 'var(--fs-label)', color: 'var(--warning)', margin: 'var(--space-2) 0 0' },
  hint: { fontFamily: 'var(--sans)', fontSize: 'var(--fs-label)', color: 'var(--muted)', marginTop: 'var(--space-4)', textAlign: 'center' },
};
